import type { CommonService } from '../common/common.service'
import type { RestangularService } from '../common/restangular.service'

import { angular, app } from '../../app.module'

interface IActivityResponse {
  activityType: string
  activityData: {
    memoId: number
    userId: number
    created: number
    message: string
    toUsers: any
    username: string
    isPrivate: number
    showMemoFlg: number
    userPictureUrl: any
  }
  objectType: string
  objectData: {
    type: string
    likes: number
    postId: string
    message: string
    snsType: string
    retweets: number
    accountId: string
    memoCount: number
    totalMedia: number
    engagements: number
    publishDate: number
    thumbnailUrl: string
    engagementRate: number
    accountScreenName: string
    postUrl: string
    linkUrl: string
    username: string
    accountUrl: string
  }
}

interface IActivityData {
  activityType: string
  activityData: {
    memoId: number
    message: string
    showMemoFlg: number
  }
  objectType: string
  objectData: {
    postId: string
    accountId: string
    snsType: string
    memoCount: number
  }
}

/**
 * アクティビティ一覧表示用のコンポーネント
 * @class ActivityList
 */
class ActivityList {
  private groupId: number
  private userId: number
  private isPrivate: boolean
  // 1ページングのアクティビティ数
  private limit: number
  private limitIndex: number
  // データ取得中フラグ
  private isLoadingFlg: boolean
  // 全件取得済みフラグ
  private isLastPagingFlg: boolean
  private activityList: IActivityData[]

  constructor(
    $rootScope,
    private $window,
    private RestangularService: RestangularService,
    private CommonService: CommonService
  ) {
    this.init()

    // メモ変数の変更があった場合、アクティビティ一覧に表示しているメモをアップデート
    $rootScope.$on('changeMemo', (event, changeInfo) => {
      setTimeout(() => {
        switch (changeInfo.type) {
          case 'add':
            this.addActivity(changeInfo)
            break
          case 'edit':
            this.editActivity(changeInfo.memo.memoId, changeInfo.memo.message)
            break
          case 'delete':
            this.deleteActivity(changeInfo.memo.memoId)
            break
        }
      })
    })
  }

  /**
   * アクティビティの高さをセット
   */
  public $onInit(): void {
    setTimeout(() => {
      const toolbarHeight = angular.element('md-toolbar').innerHeight()
      const activityHeaderHeight = angular.element('.activity-header').innerHeight()

      this.setActivityHeight(toolbarHeight, activityHeaderHeight)

      // ウィンドウサイズが変更された時に実行
      angular.element(this.$window).bind('resize', () => {
        this.setActivityHeight(toolbarHeight, activityHeaderHeight)
      })
    })
  }

  /**
   * グループIDまたはユーザーIDが変更されると、アクティビティ一覧を取得
   */
  public $onChanges(): void {
    // グループIDまたはユーザーIDがundefinedの場合、リターン
    if (this.groupId === undefined || this.userId === undefined) {
      return
    }

    this.getActivityList()
  }

  /**
   * スクロール時、次のアクティビティ一覧を取得
   */
  public loadMore(): void {
    // 全件取得済みの場合またはデータ取得中の場合、リターン
    if (this.isLastPagingFlg === true || this.isLoadingFlg === true) {
      return
    }
    this.isLoadingFlg = true
    this.limitIndex = this.limitIndex + this.limit
    this.getActivityList()
  }

  /**
   * プライベートメモフラグを変更すると、アクティビティ一覧を取得
   */
  public changeFilter(): void {
    this.init()
    this.getActivityList()
  }

  /**
   * 初期化
   */
  private init(): void {
    this.limit = 10
    this.limitIndex = 0
    this.isLastPagingFlg = false
    this.isLoadingFlg = true
    this.activityList = []
  }

  /**
   * アクティビティ一覧を取得
   */
  private getActivityList(): void {
    // メモ一覧を取得
    const endpoint = 'activity/list'
    const params = {
      group_id: this.groupId,
      user_id: this.userId,
      is_private: this.isPrivate ? 1 : 0,
      limit_index: this.limitIndex,
      limit: this.limit,
    }

    this.RestangularService.request('GET', endpoint, params).then((result) => {
      // アクティビティ数より多い場合、全件取得済みフラグをtrueにする
      if (result[0].data.length < this.limit) {
        this.isLastPagingFlg = true
      }

      this.activityList = this.activityList.concat(this.setActivityList(result[0].data))
      this.isLoadingFlg = false
    })
  }

  /**
   * アクティビティ一覧をセット
   * @param {IActivityResponse[]} activities アクティビティデータ
   * @return {IActivityData[]} セットしたアクティビティデータ
   */
  private setActivityList(activities: IActivityResponse[]): IActivityData[] {
    // アクティビティ数が0の場合、空配列をリターン
    if (activities.length === 0) {
      return []
    }

    return activities.map((activity) => {
      const activityData = {
        activityType: activity.activityType,
        activityData: activity.activityData,
        objectType: activity.objectType,
        objectData: activity.objectData,
      }

      if (activityData.objectData.snsType === 'instagram') {
        activityData.objectData.thumbnailUrl = activityData.objectData.linkUrl + '/media?size=m'
      }

      // アカウントの場合、SNSでアカウント確認用のURLをセット
      if (activity.objectType === 'account') {
        activityData.objectData.accountUrl = this.CommonService.setAccountUrl(
          activityData.objectData.snsType,
          activityData.objectData.accountId,
          activityData.objectData.username
        )
      } else {
        activityData.objectData.postUrl = this.CommonService.setPostUrl(
          activityData.objectData.snsType,
          activityData.objectData.accountId,
          activityData.objectData.postId,
          activityData.objectData.linkUrl,
          activityData.objectData.type,
          activityData.objectData.username
        )
      }

      return activityData
    })
  }

  /**
   * 新しいアクティビティを追加
   * @param {object} memoData 新しいメモ
   */
  private addActivity(memoData: {
    memo: {}
    objectType: string
    objectId: string
    snsType: string
  }): void {
    for (let i = 0; i < this.activityList.length; i = i + 1) {
      const validObjectType = this.activityList[i].objectType === memoData.objectType
      const validSnsType = this.activityList[i].objectData.snsType === memoData.snsType
      const validObjectId =
        this.activityList[i].objectData.postId === memoData.objectId ||
        this.activityList[i].objectData.accountId === memoData.objectId

      // 同じメモ対象の場合
      if (validObjectType && validSnsType && validObjectId) {
        // $$hashKeyを解除
        const newActivity = JSON.parse(angular.toJson(this.activityList[i]))

        // アクティビティ内容をセット
        newActivity.activityData = memoData.memo
        // アクティビティ一覧の上に追加
        this.activityList.unshift(newActivity)
        break
      }
    }
  }

  /**
   * アクティビティの内容をアップデート
   * @param {number} memoId メモID
   * @param {string} message メモ内容
   */
  private editActivity(memoId: number, message: string): void {
    // 変更したアクティビティを取得
    const editActivity = this.activityList.filter((activity) => {
      return activity.activityData.memoId === memoId
    })

    // アクティビティの内容をアップデート
    if (editActivity && editActivity[0]) {
      editActivity[0].activityData.message = message
    }
  }

  /**
   * アクティビティの表示フラグを0にする
   * @param {number} memoId メモID
   */
  private deleteActivity(memoId: number): void {
    // 削除したアクティビティを取得
    const deleteActivity = this.activityList.filter((activity) => {
      return activity.activityData.memoId === memoId
    })

    // アクティビティの表示フラグを0にする
    if (deleteActivity && deleteActivity[0]) {
      deleteActivity[0].activityData.showMemoFlg = 0
    }
  }

  /**
   * アクティビティの高さをセット
   * @param {number} toolbarHeight ツールバーの高さ
   * @param {number} activityHeaderHeight アクティビティヘッダの高さ
   */
  private setActivityHeight(toolbarHeight: number, activityHeaderHeight: number): void {
    // アクティビティの高さをセット(16: md-contentのmargin-topとmargin-bottom; 8: md-cardのmargin-topとmargin-bottom)
    const activityHeight = this.$window.innerHeight - toolbarHeight - (16 + 8) * 2

    angular.element('.activity').css('height', activityHeight)
    // アクティビティ一覧の高さを計算してセット
    angular.element('.activity-list').css('height', activityHeight - activityHeaderHeight)
  }
}

app.component('activityList', {
  bindings: {
    groupId: '<',
    userId: '<',
  },
  template: require('./activity-list.pug')(),
  controller: ActivityList,
})
